import { useEffect, useCallback, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

const ZOOM = 2.2;

export const Lightbox = ({ shots, index, onClose, onChange }) => {
  const open = index !== null;
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");
  const prev = useCallback(() => onChange((index + shots.length - 1) % shots.length), [index, shots.length, onChange]);
  const next = useCallback(() => onChange((index + 1) % shots.length), [index, shots.length, onChange]);

  useEffect(() => setZoomed(false), [index]);

  const pointOrigin = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const p = e.touches?.[0] ?? e;
    const x = ((p.clientX - r.left) / r.width) * 100;
    const y = ((p.clientY - r.top) / r.height) * 100;
    setOrigin(`${Math.min(100, Math.max(0, x))}% ${Math.min(100, Math.max(0, y))}%`);
  };
  const toggleZoom = (e) => {
    e.stopPropagation();
    pointOrigin(e);
    setZoomed((z) => !z);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open, onClose, prev, next]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="lightbox"
          data-testid="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={shots[index].title}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0a1220]/90 backdrop-blur-md"
          onClick={onClose}
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="lightbox-close"
            aria-label="Close"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            <X size={20} />
          </button>

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            data-testid="lightbox-prev"
            aria-label="Previous screenshot"
            className="absolute left-2 sm:left-8 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-[var(--electric)] transition-colors"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            data-testid="lightbox-next"
            aria-label="Next screenshot"
            className="absolute right-2 sm:right-8 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-[var(--electric)] transition-colors"
          >
            <ChevronRight size={22} />
          </button>

          <div className="flex flex-col items-center gap-4 px-16 sm:px-24" onClick={(e) => e.stopPropagation()}>
            <div
              className={`relative overflow-hidden rounded-[1.5rem] border border-white/10 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)] ${zoomed ? "cursor-zoom-out" : "cursor-zoom-in"}`}
              onClick={toggleZoom}
              onMouseMove={zoomed ? pointOrigin : undefined}
              onTouchMove={zoomed ? pointOrigin : undefined}
              data-testid="lightbox-zoom-area"
              data-zoomed={zoomed}
            >
              <div style={{ transform: zoomed ? `scale(${ZOOM})` : "scale(1)", transformOrigin: origin, transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)" }}>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.img
                    key={shots[index].src}
                    src={shots[index].src}
                    alt={`${shots[index].title} — ${shots[index].sub}`}
                    data-testid="lightbox-image"
                    draggable={false}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="max-h-[78vh] w-auto block select-none"
                  />
                </AnimatePresence>
              </div>
              <span className="pointer-events-none absolute bottom-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#0a1220]/70 text-white">
                {zoomed ? <ZoomOut size={15} /> : <ZoomIn size={15} />}
              </span>
            </div>
            <div className="flex items-center gap-2" data-testid="lightbox-dots">
              {shots.map((s, i) => (
                <button
                  key={s.src}
                  type="button"
                  aria-label={`Go to ${s.title}`}
                  onClick={() => onChange(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-[var(--clinical)]" : "w-1.5 bg-white/30 hover:bg-white/60"}`}
                />
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
